/**
 * @file statementNotes.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-20
 * @modified 2026-10-20
 *
 * A statement's two notes, as Generate Statements shows them (Cam, 20 Oct
 * 2026: "allow notes for office only visibility (which show in the approval
 * table) and also notes to go on the statement and in the email body"):
 *
 *   office_note  the office's own — in the approval table, the preview's notice
 *                and the note window; never on the statement, in the email or
 *                an export (services/statementNoteService).
 *   note         for the customer — printed on the statement (email and print
 *                layouts, "For your attention") and in the email body.
 *
 * The words, the "has a note" tests, who-last-changed-it and the preview's key
 * (so a just-saved customer note shows on the next preview, without
 * regenerating) live here, once.
 */

export const NOTE_FIELDS = {
  office_note: {
    label: 'Office note — only seen here',
    short: 'Office note',
    hint: 'For whoever works on this run: shown in the approval table and here. Never printed, emailed or exported.',
    placeholder: 'e.g. Rang Tuesday — paying Friday; hold until then.',
    icon: 'sticky_note_2'
  },
  note: {
    label: 'Note for the customer — printed on the statement and in the email',
    short: 'On statement',
    hint: 'Printed on the statement in a “For your attention” box (email and print layouts), and added to the email body when it is emailed.',
    placeholder: 'e.g. Thank you for your payment of £1,200 on 2 October.',
    icon: 'description'
  }
};

/** Longest note (the customer note prints up to 2,000 characters). */
export const MAX_NOTE_CHARS = 2000;

const text = (v) => (v == null ? '' : String(v).trim());

export const hasOfficeNote = (s) => !!text(s?.office_note);
export const hasCustomerNote = (s) => !!text(s?.note);
/** The Review "Notes" filter: either note. */
export const hasAnyNote = (s) => hasOfficeNote(s) || hasCustomerNote(s);

/** "Last changed by Jade Ward, Mon 20 Oct, 09:10" — or null. */
export function lastChangedText(name, at) {
  if (!name) return null;
  const d = at ? new Date(at) : null;
  const when = d && !Number.isNaN(d.getTime())
    ? `, ${d.toLocaleString('en-GB', { timeZone: 'Europe/London', weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}`
    : '';
  return `Last changed by ${name}${when}`;
}

/**
 * A short key for the customer note as it now reads — part of the preview's
 * key, so the preview is drawn again when the note changes. The office note
 * is never part of it (it is not on the statement).
 */
export function previewNoteKey(note) {
  const t = text(note);
  if (!t) return '0';
  let h = 5381;
  for (let i = 0; i < t.length; i += 1) h = ((h * 33) ^ t.charCodeAt(i)) >>> 0;
  return `${t.length}.${h.toString(36)}`;
}
